import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getCommission,
  getCommissionUploadDetail,
  getPayment,
  postCommissionInvoiceUpload,
} from "../../../data";

import {
  Link,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FileInput } from "@/components/ui/file-input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { isFormCompanyType } from "@/utils/company-type-options";
import { CommissionInvoiceFormScreen } from "./upload-form";
import {
  BrandHeader,
  ManagerContact,
  SummaryList,
  SummaryRow,
} from "./form-branding";
import { formatTRY } from "@/utils/currency";

export function CommissionInvoiceUploadScreen() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [screenLoading, setScreenLoading] = useState(false);

  const currentPath = useResolvedPath();

  const pathname = useResolvedPath().pathname.split("/")[2];
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["commissionDetail", pathname],

    queryFn: async () => await getCommissionUploadDetail(pathname),
    retry: 1,
  });

  const mutation = useMutation({
    mutationKey: ["uploadPaymentInvoice"],

    mutationFn: (data) => {
      return postCommissionInvoiceUpload(data);
    },
    onSuccess: (response) => {
      setScreenLoading(false);
      //navigate("/payments");
      if (response.status === 200) {
        queryClient.invalidateQueries(["uploadPaymentInvoice", pathname]);
      }
    },
    onError: (error) => {
      setScreenLoading(false);
      const errorData = error?.response?.data;
      if (errorData?.alreadyUploaded) {
        toast({
          title: "Fatura Zaten Yüklendi",
          description: "Bu ödeme için zaten bir fatura yüklenmiş.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Hata",
          description: errorData?.error || "Bir şeyler ters gitti. Lütfen daha sonra tekrar deneyin.",
          variant: "destructive",
        });
      }
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const onSubmit = async (values) => {
    setScreenLoading(true);
    console.log(values);
    const { pdfFile } = values;

    if (pdfFile && pdfFile[0].type !== "application/pdf") {
      setScreenLoading(false);
      toast({
        title: "Geçersiz dosya tipi",
        description: "Lütfen sadece PDF dosyası yükleyin.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.append("fileName", `${data.data.influencerName}-${data.data.id}`);
    formData.append("file", pdfFile[0]);

    mutation.mutate({
      path: pathname,
      data: formData,
      invoiceId: data.data.payment.invoiceId,
    });
  };

  if (!isLoading && !screenLoading) {
    if (data.data.country != null) {
      if (data.data.country == "Azerbaycan") {
        navigate("/invoiceManuel/" + pathname);
      }
    }
  }

  if (isLoading || screenLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <BrandHeader />
            <CardTitle>Fatura Yükle</CardTitle>
            <CardDescription>
              İsteğiniz işlenirken lütfen bekleyin
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <div
                className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                  Yükleniyor...
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <BrandHeader />
            <CardTitle>Hata</CardTitle>
            <CardDescription>
              Bir şeyler ters gitti. Lütfen daha sonra tekrar deneyin. Sorun devam ederse destek ekibimizle iletişime geçin.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // istisna / gider pusulası -> fatura yükleme yerine bilgi formu
  if (isFormCompanyType(data.data.client?.companyType)) {
    if (
      data.data.status == "Auto Created Invoice" ||
      data.data.status == "Invoice Uploaded"
    ) {
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <BrandHeader />
              <CardTitle>Teşekkürler</CardTitle>
              <CardDescription>
                Ödeme bilgileriniz tarafımıza ulaştı. Bu sayfayı
                kapatabilirsiniz.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ManagerContact payment={data.data.payment} />
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <CommissionInvoiceFormScreen path={pathname} commission={data.data} />
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      {data.data.status == "Invoice Uploaded" ? (
        <Card className="w-full max-w-md">
          <CardHeader>
            <BrandHeader />
            <CardTitle>Başarılı</CardTitle>
            <CardDescription>
              Faturanız başarıyla gönderildi. Bu sayfayı kapatabilirsiniz.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ManagerContact payment={data.data.payment} />
          </CardContent>
        </Card>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-md">
          <Card>
            <CardHeader>
              <BrandHeader />
              <CardTitle>Fatura Yükle</CardTitle>
              <CardDescription>
                Faturanızı aşağıdan yükleyebilirsiniz
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ManagerContact payment={data.data.payment} />

              <SummaryList>
                <SummaryRow
                  label="Proje / Ödeme"
                  value={data.data.payment?.name}
                />
                <SummaryRow label="E-posta" value={data.data.email} />
                <SummaryRow
                  label="Fatura İçin Net Tutar"
                  value={formatTRY(data.data.finalAmount)}
                  emphasis
                />
              </SummaryList>

              <div className="space-y-1.5">
                <Label htmlFor="pdfFile">Fatura PDF'i</Label>
                <FileInput
                  id="pdfFile"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && file.type !== "application/pdf") {
                      toast({
                        title: "Geçersiz dosya tipi",
                        description: "Lütfen sadece PDF dosyası seçin.",
                        variant: "destructive",
                      });
                      e.target.value = null; // Clear the input
                    } else {
                      setValue("pdfFile", e.target.files);
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Yalnızca PDF dosyası kabul edilmektedir.
                </p>
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" className="w-full">
                Faturayı Gönder
              </Button>
            </CardFooter>
          </Card>
        </form>
      )}
    </div>
  );
}
